import { useCallback, useEffect, useState } from 'react';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { IconButton } from '@mui/material';
import { convertsCentsToCurrency } from '@npi/buddy-utils';

import { navigate, routes } from '@redwoodjs/router';

import DateFilterMenuItem from 'src/components/FilterDropDown/components/DateFilterMenuItem';
import {
    buildFilters,
    createFilterObject,
    DateRangeDetails,
} from 'src/components/FilterDropDown/utils';
import { Flex } from 'src/components/Flex';
import {
    NPIDataGrid,
    NPIDataGridColDef,
    NPIDataGridRow,
} from 'src/components/NPIDataGrid/NPIDataGrid';
import { createColumnDef } from 'src/components/NPIDataGrid/NPIDataGrid.utils';
import { ACCOUNT_STATEMENTS_PAGINATED } from 'src/graphql/queries/accountStatements.query';
import useStablePagination from 'src/hooks/useStablePagination';
import useTable from 'src/hooks/useTable';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { formatDate } from 'src/utils/utils';

type Statement = {
    id: number;
    accountStatementId: string;
    merchantName: string;
    merchantAccountNumber: string;
    statementDate: string;
    outstandingBalance: number;
};

function AccountStatementsPage() {
    const maxHeight = '400';
    const [tableData, setTableData] = useState([]);

    const {
        rows: data,
        pagination,
        fetchMore,
        updateSorting,
        updateFilters,
        loading,
        sortState,
        filterState,
        reset,
    } = useTable({
        query: ACCOUNT_STATEMENTS_PAGINATED,
        pagination: {
            page: 1,
            pageSize: 5,
        },
        sorting: [{ field: 'accountStatementId', sort: 'desc' }],
        filterModel: { items: [] },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    useEffect(() => {
        if (data && data.length > 0) {
            const mappedData =
                data?.map((item) => {
                    return {
                        ...item,
                        merchantName: item?.merchant?.tradingName,
                        merchantAccountNumber: item?.merchant?.accountNumber,
                        outstandingBalance: `${
                            item?.merchant?.currencySymbol
                        }${convertsCentsToCurrency(item?.outstandingBalance)}`,
                    };
                }) ?? [];
            setTableData(mappedData);
        } else {
            // Clear table when no results
            setTableData([]);
        }
    }, [data]);

    const openView = useCallback((statement: Statement) => {
        navigate(routes.viewAccountStatement({ id: statement.id }));
    }, []);

    const { stableTotalRows, stablePage } = useStablePagination(pagination, loading);

    const STATEMENTS_TABLE_HEADERS: NPIDataGridColDef[] = [
        createColumnDef({
            field: 'accountStatementId',
            headerName: 'Statement ID',
        }),
        createColumnDef({
            field: 'merchantName',
            headerName: 'Merchant Name',
        }),
        createColumnDef({
            field: 'merchantAccountNumber',
            headerName: 'Merchant Account Number',
        }),
        createColumnDef({
            field: 'statementDate',
            headerName: 'Statement Date',
            renderCell: (params) => {
                const date = new Date(params?.row?.statementDate);
                return formatDate({ date });
            },
        }),
        createColumnDef({
            field: 'outstandingBalance',
            headerName: 'Outstanding Balance',
        }),
        createColumnDef({
            field: 'view',
            flex: 0,
            minWidth: 160,
            renderCell: (params) => {
                return (
                    <Flex gap="8px">
                        <IconButton
                            aria-label="view"
                            data-testid="view"
                            onClick={() => {
                                openView(params.row);
                            }}
                        >
                            <VisibilityOutlinedIcon />
                        </IconButton>
                    </Flex>
                );
            },
        }),
    ];
    const handleOnClearFilters = () => {
        setFromDate(null);
        setToDate(null);
        reset();
    };

    const handleOnApplyFilters = () => {
        const dateDetails: DateRangeDetails = {
            field: 'statementDate',
            fromDate,
            toDate,
        };
        const model = createFilterObject(buildFilters(dateDetails));
        updateFilters(model);
    };

    return (
        <PageContent
            heading="Account Statements"
            subHeading="Full account statements showing Invoice, Payments and Credit Notes"
        >
            <NPIDataGrid
                key="account-statements-table_x"
                dataTestId="account-statements-table"
                rows={tableData as unknown as NPIDataGridRow[]}
                columns={STATEMENTS_TABLE_HEADERS}
                getRowId={(row) => {
                    return row.id;
                }}
                handleView={openView}
                serverSideFiltering={{
                    onFilterChange: updateFilters,
                    filterModel: filterState,
                }}
                serverSideSorting={{
                    onSortChange: updateSorting,
                    sortModel: sortState,
                }}
                pagination={{
                    isEnabled: true,
                    mode: 'server',
                    page: stablePage - 1,
                    pageSize: pagination.pageSize,
                    totalRows: stableTotalRows,
                    onPageChange: (page, pageSize) => {
                        fetchMore({ page: page + 1, pageSize });
                    },
                }}
                addFilterButtonConfig={{
                    isFilterActive: fromDate !== null || toDate !== null,
                    handleOnClearFilters,
                    handleOnApplyFilters,
                    filters: [
                        <DateFilterMenuItem
                            key="credit-date-filter"
                            title="Date Added"
                            fromDate={fromDate}
                            toDate={toDate}
                            handleFromDateChange={setFromDate}
                            handleToDateChange={setToDate}
                            onClearFilter={() => {
                                setFromDate(null);
                                setToDate(null);
                            }}
                        />,
                    ],
                }}
                title="Account Statements"
                maxHeight={maxHeight}
                handleReset={reset}
                filtering={{ isEnabled: true }}
                loading={loading}
            />
        </PageContent>
    );
}

export default AccountStatementsPage;
